import React from "react";
//#region types
import { TodoIncomingInvoiceTypeSectionProps } from "models/propTypes/missingDocumentPropTypes";
import { TranslationKeys } from "translations";
import { SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { PeriodisationProps } from "models/propTypes/invoicePropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { CurrencyEnums, DimensionEnums } from "models/enums";
//#endregion
//#region components
import {
  DimensionChipSelector,
  ExpandableSection,
  ExpandableSectionSummary,
  FormFieldLayout,
  SearchInput,
  VerticalFormField,
} from "components/common/wintForms";
import { Periodisation } from "components/common";
import { useBookingTreeItems } from "components/incomingInvoice";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const TodoIncomingInvoiceTypeSection: React.FC<TodoIncomingInvoiceTypeSectionProps> = ({
  values,
  connect,
  amount,
  date,
  hasDimensions,
  fileViewer,
}) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const { searchBookingTreeItemsAutoComplete: searchBookingTreeItems } = useBookingTreeItems();

  const [allowPeriodisation, setAllowPeriodisation] = React.useState(false);

  // The server owns the threshold, since a company can be allowed to periodise below it in Heart.
  // The amount is the bank payment in SEK, so the chosen invoice currency doesn't affect it.
  React.useEffect(() => {
    const absoluteAmount = Math.abs(amount ?? 0);
    if (!absoluteAmount) {
      setAllowPeriodisation(false);
      return;
    }

    let isCurrent = true;
    incomingInvoiceService
      .allowPeriodisations(absoluteAmount, CurrencyEnums.CurrencyCode.SEK, date ? new Date(date) : new Date())
      .then((allowed) => isCurrent && setAllowPeriodisation(allowed))
      .catch(() => isCurrent && setAllowPeriodisation(false));
    return () => {
      isCurrent = false;
    };
  }, [amount, date]);

  return (
    <>
      <ExpandableSection alwaysOpen>
        <VerticalFormField<SearchInputProps>
          Component={SearchInput}
          {...connect("incomingInvoice.category")}
          label={TranslationKeys.IncomingInvoice.InvoiceCategoryText}
          icon={IconDefinitionConstants.WVat}
          items={searchBookingTreeItems}
        />
      </ExpandableSection>
      {hasDimensions ? (
        <ExpandableSection
          summary={
            <ExpandableSectionSummary
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
            />
          }
        >
          <DimensionChipSelector
            {...connect("incomingInvoice.dimensions")}
            type={DimensionEnums.DimensionCategory.IncomingInvoice}
            allowDefaultDimensions
            amount={Math.abs(amount ?? 0)}
            currency={values.currency}
            layout={<FormFieldLayout direction="vertical" />}
            fileViewer={fileViewer}
          />
        </ExpandableSection>
      ) : (
        <></>
      )}

      {allowPeriodisation ? (
        <ExpandableSection
          summary={
            <ExpandableSectionSummary
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
            />
          }
        >
          <VerticalFormField<PeriodisationProps>
            Component={Periodisation}
            {...connect("incomingInvoice.periodisation")}
            label={TranslationKeys.Common.Period}
          />
        </ExpandableSection>
      ) : (
        <></>
      )}
    </>
  );
};

export default TodoIncomingInvoiceTypeSection;
